import { useMemo } from 'react';
import { formatTime, convertTime } from '../lib/constants';

/**
 * DayColumnViz
 *
 * Props:
 *   row          — row object { label, days, ... }
 *   selectedTimes — string[] of "HH:MM" in pollTz
 *   duration      — meeting duration in minutes
 *   use12h        — boolean
 *   voterData     — { [pollTzTime]: { yes, no, ifNeedBe, didntVote } }
 *   hoveredTime   — "HH:MM" in pollTz of the currently hovered option (for pulse)
 *   pollTz        — IANA tz string of the poll organizer (source tz)
 *   displayTz     — IANA tz string to display times in (may differ from pollTz)
 */
export default function DayColumnViz({
  row,
  selectedTimes = [],
  duration = 60,
  use12h = true,
  voterData = null,
  hoveredTime = null,
  pollTz = null,
  displayTz = null,
}) {
  if (!row || selectedTimes.length === 0) return null;

  const needsConversion = pollTz && displayTz && pollTz !== displayTz;

  // Build a converted block list from the poll-tz selectedTimes.
  // Each block carries both the original key (for voterData lookup) and
  // the converted display time + day shift.
  const convertedBlocks = useMemo(() => {
    return selectedTimes.map(origTime => {
      if (!needsConversion) {
        const parts = origTime.split(':').map(Number);
        return {
          origTime,
          displayTime: origTime,
          startMins: parts[0] * 60 + parts[1],
          dayShift: 0,
        };
      }
      const { time: displayTime, dayShift } = convertTime(origTime, pollTz, displayTz);
      const parts = displayTime.split(':').map(Number);
      // Shift startMins by dayShift so blocks crossing midnight render correctly.
      // We keep everything in a single linearised minute space:
      // previous day = negative, same day = 0-1439, next day = 1440-2879
      const baseMins = parts[0] * 60 + parts[1];
      const startMins = baseMins + dayShift * 1440;
      return { origTime, displayTime, startMins, dayShift };
    });
  }, [selectedTimes, needsConversion, pollTz, displayTz]);

  // Sort by converted startMins
  const sortedBlocks = useMemo(() =>
    [...convertedBlocks].sort((a, b) => a.startMins - b.startMins),
  [convertedBlocks]);

  const firstMins = sortedBlocks[0].startMins;
  const lastMins  = sortedBlocks[sortedBlocks.length - 1].startMins + duration;

  // Round axis range to whole hours with 1h padding each side
  const startMins = Math.floor((firstMins - 60) / 60) * 60;
  const endMins   = Math.ceil((lastMins  + 60) / 60) * 60;
  const rangeMins = endMins - startMins;

  const hourLines = useMemo(() => {
    const lines = [];
    for (let m = startMins; m <= endMins; m += 60) lines.push(m);
    return lines;
  }, [startMins, endMins]);

  const COL_HEIGHT = 280;
  const LABEL_W    = 62;
  const minsToY    = mins => ((mins - startMins) / rangeMins) * COL_HEIGHT;
  const blockH     = mins => (mins / rangeMins) * COL_HEIGHT;

  // Sub-column overlap assignment (reset to col 0 when gap appears)
  const withCols = useMemo(() => {
    const result = sortedBlocks.map(b => ({
      ...b,
      endMins: b.startMins + duration,
      col: 0,
      totalCols: 1,
    }));
    const colEnds = [];
    for (let i = 0; i < result.length; i++) {
      const b = result[i];
      let assigned = -1;
      for (let c = 0; c < colEnds.length; c++) {
        if (b.startMins >= colEnds[c]) { assigned = c; break; }
      }
      if (assigned === -1) assigned = colEnds.length;
      result[i].col = assigned;
      colEnds[assigned] = b.endMins;
    }
    const maxCol = Math.max(...result.map(b => b.col));
    result.forEach(b => { b.totalCols = maxCol + 1; });
    return result;
  }, [sortedBlocks, duration]);

  const VOTE_COLORS = {
    yes:       { bg: 'rgba(68,215,168,0.25)',  border: 'var(--green)',   text: 'var(--green)' },
    no:        { bg: 'rgba(255,77,106,0.25)',   border: 'var(--red)',    text: 'var(--red)' },
    ifNeedBe:  { bg: 'rgba(245,166,35,0.25)',   border: 'var(--amber)',  text: 'var(--amber)' },
    didntVote: { bg: 'rgba(44,48,96,0.4)',      border: 'var(--border2)', text: 'var(--muted)' },
    none:      { bg: 'rgba(124,111,247,0.18)',  border: 'var(--accent)', text: 'var(--accent)' },
  };

  // voterData is keyed by origTime (poll tz)
  const getBlockStyle = (origTime) => {
    if (!voterData || !voterData[origTime]) return VOTE_COLORS.none;
    const v = voterData[origTime];
    if (v.yes      && v.yes.length > 0)      return VOTE_COLORS.yes;
    if (v.ifNeedBe && v.ifNeedBe.length > 0) return VOTE_COLORS.ifNeedBe;
    if (v.no       && v.no.length  > 0)      return VOTE_COLORS.no;
    if (v.didntVote && v.didntVote.length > 0) return VOTE_COLORS.didntVote;
    return VOTE_COLORS.none;
  };

  // Day-shift label for blocks that crossed midnight
  const dayShiftNote = (shift) =>
    shift === 1 ? ' +1d' : shift === -1 ? ' −1d' : '';

  // Row label: show tz info if conversion is happening
  const tzNote = needsConversion ? ` (${displayTz})` : '';

  return (
    <div className="card">
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>Time view</div>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: 'var(--text)' }}>
        {row.label}
      </div>
      {needsConversion && (
        <div style={{ fontSize: 10, color: 'var(--accent2)', marginBottom: 10 }}>
          Times shown in your selected timezone
        </div>
      )}

      {/* Legend */}
      {voterData && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
          {[
            { key: 'yes',       label: '✓ Yes' },
            { key: 'ifNeedBe',  label: '~ If need be' },
            { key: 'no',        label: '✗ No' },
            { key: 'didntVote', label: '— No response' },
          ].map(({ key, label }) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10 }}>
              <div style={{ width: 8, height: 8, borderRadius: 2, background: VOTE_COLORS[key].bg, border: '1px solid ' + VOTE_COLORS[key].border }} />
              <span style={{ color: 'var(--muted)' }}>{label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Column layout */}
      <div style={{ position: 'relative', height: COL_HEIGHT + 'px', marginLeft: LABEL_W + 'px' }}>

        {/* Hour gridlines + labels */}
        {hourLines.map(m => {
          const y = minsToY(m);
          // Convert linearised minutes back to a clock time for the label.
          // m may be negative (previous day) or ≥1440 (next day) — normalise.
          const clockMins = ((m % 1440) + 1440) % 1440;
          const hh = Math.floor(clockMins / 60);
          const mm = clockMins % 60;
          const lbl = formatTime(
            String(hh).padStart(2,'0') + ':' + String(mm).padStart(2,'0'),
            use12h
          );
          const crossDay = m < 0 ? '−1d ' : m >= 1440 ? '+1d ' : '';
          return (
            <div key={m} style={{ position: 'absolute', top: y + 'px', left: 0, right: 0, pointerEvents: 'none' }}>
              <div style={{
                position: 'absolute',
                left: -(LABEL_W),
                width: LABEL_W - 6,
                fontSize: 9,
                color: 'var(--muted)',
                whiteSpace: 'nowrap',
                transform: 'translateY(-50%)',
                textAlign: 'right',
              }}>
                {crossDay}{lbl}
              </div>
              <div style={{ position: 'absolute', left: 0, right: 0, borderTop: '1px solid var(--border)', opacity: 0.5 }} />
            </div>
          );
        })}

        {/* Time blocks */}
        {withCols.map(({ origTime, displayTime, startMins: sM, endMins: eM, dayShift, col, totalCols }) => {
          const y         = minsToY(sM);
          const h         = blockH(eM - sM);
          const style     = getBlockStyle(origTime);
          const colW      = 100 / totalCols;
          const votes     = voterData && voterData[origTime];
          const isHovered = hoveredTime === origTime; // compare on poll-tz origTime

          return (
            <div
              key={origTime}
              className={isHovered ? 'daycol-block daycol-block--pulse' : 'daycol-block'}
              style={{
                position: 'absolute',
                top:    y + 'px',
                left:   (col * colW) + '%',
                width:  colW + '%',
                height: Math.max(h - 2, 14) + 'px',
                background: style.bg,
                border: '1px solid ' + style.border,
                borderRadius: 5,
                boxSizing: 'border-box',
                padding: '2px 4px',
                overflow: 'hidden',
                transition: 'border-color 0.2s',
                zIndex: isHovered ? 2 : 1,
                boxShadow: isHovered
                  ? '0 0 0 2px ' + style.border + ', 0 0 8px 1px ' + style.border
                  : 'none',
              }}
            >
              <div style={{ fontSize: 9, fontWeight: 600, color: style.text, whiteSpace: 'nowrap' }}>
                {formatTime(displayTime, use12h)}{dayShiftNote(dayShift)}
              </div>
              {votes && h > 28 && (
                <div style={{ fontSize: 8, color: style.text, marginTop: 1, lineHeight: 1.3 }}>
                  {votes.yes      && votes.yes.length > 0      && <div>✓ {votes.yes.join(', ')}</div>}
                  {votes.ifNeedBe && votes.ifNeedBe.length > 0 && <div>~ {votes.ifNeedBe.join(', ')}</div>}
                  {votes.no       && votes.no.length  > 0      && <div>✗ {votes.no.join(', ')}</div>}
                  {votes.didntVote && votes.didntVote.length > 0 && (
                    <div style={{ color: 'var(--muted)' }}>— {votes.didntVote.join(', ')}</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 10, textAlign: 'center' }}>
        {duration}-min blocks · {sortedBlocks.length} option{sortedBlocks.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}
